import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import DeleteModal from "./DeleteModal";
import ListAdminButtons from "../Common/ListAdminButtons";
import ResultModal from "../Common/ResultModal";
import { useTranslation } from "react-i18next";
import { GlobalContext } from "../../Contexts/GlobalContextProvider";
import { unitOfMeasureServices } from "../../Services";
import SpinnerLoading from "../Common/SpinnerLoading";
import { AuthContext } from "../../Contexts/AuthContext";
import { roles } from "../../Config/Roles";
import Tooltip from "../Common/Tooltip";

const UnitOfMeasure = () => {
  const { t: translator } = useTranslation();
  const { updateLocTab, updateLocName, isMobile } = useContext(GlobalContext);
  const { roleId } = useContext(AuthContext);
  const { master, admin } = roles;
  const history = useHistory();

  const [pagination, setPagination] = useState({
    totalCount: 0,
    pageSize: 10,
    pageIndex: 0,
  });
  const [resultModal, setResultModal] = useState({
    title: "",
    text: "",
    show: false,
    btnClass: "",
  });
  const [unitOfMeasures, setUnitOfMeasures] = useState([]);
  const [update, setUpdate] = useState(new Date());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState([]);
  const [filters, setFilters] = useState({
    searchText: "",
  });

  useEffect(() => {
    updateLocName("unit-of-measures");
    unitOfMeasureServices
      .getSearch({
        searchText: filters.searchText,
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      })
      .then((data) => {
        setIsLoading(false);
        setUnitOfMeasures(data.data.data);
        setPagination({ ...pagination, totalCount: data.data.totalCount });
      });
    updateLocTab(0);
  }, [filters, update]);

  useEffect(() => {}, [unitOfMeasures]);

  const handleToAdd = () => {
    history.push("/add-unit-of-measure");
  };

  const handleToUpdate = () => {
    if (selectedItems.length !== 1) return false;
    history.push(`/edit-unit-of-measure/${selectedItems}`);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFilters({ ...filters, [name]: value });
  };

  const handleHideDeleteModal = () => setShowDeleteModal(false);

  const handleCloseResultModal = () => {
    setResultModal({ show: false });
  };

  const confirmDelete = () => {
    let deletePromises = [];

    selectedItems.forEach((id) => {
      deletePromises.push(unitOfMeasureServices.deleteUnitOfMeasure(id));
    });

    Promise.all(deletePromises)
      .then((res) => {
        setUpdate(new Date());
        setShowDeleteModal(false);
        setSelectedItems([]);
        const hasErrors = res.some((x) => !x.data.success);
        !hasErrors
          ? setResultModal({
              show: true,
              title: translator("UnitOfMeasure-SuccessDeleteModal-Title"),
              text: translator("UnitOfMeasure-SuccessDeleteModal-Text"),
              btnClass: "btn btn-primary",
            })
          : setResultModal({
              show: true,
              title: translator("UnitOfMeasure-UnsuccessDeleteModal-Title"),
              text: translator("UnitOfMeasure-UnsuccessDeleteModal-Text"),
              btnClass: "btn btn-primary",
            });
      })
      .catch((err) =>
        setResultModal({
          show: true,
          title: translator("UnitOfMeasure-UnsuccessDeleteModal-Title"),
          text: translator("UnitOfMeasure-UnsuccessDeleteModal-Text"),
          btnClass: "btn btn-primary",
        })
      );
  };

  const handleOnSelect = (row, isSelect) => {
    if (isSelect) {
      setSelectedItems([...selectedItems, row.unitOfMeasureId]);
    } else {
      setSelectedItems(selectedItems.filter((p) => p !== row.unitOfMeasureId));
    }
  };

  const handleOnSelectAll = (isSelect) => {
    if (isSelect) {
      setSelectedItems(unitOfMeasures.map((x) => x.unitOfMeasureId));
    } else {
      setSelectedItems([]);
    }
  };

  const handleTableChange = (type, { page, sizePerPage }) => {
    setPagination({
      ...pagination,
      pageIndex: page - 1,
      pageSize: sizePerPage,
    });
    setUpdate(new Date());
  };

  const RemoteAll = ({ data, page, sizePerPage, totalSize, onTableChange }) => (
    <div>
      <BootstrapTable
        remote
        keyField="unitOfMeasureId"
        data={data}
        columns={columns}
        noDataIndication={translator("No-Data")}
        rowClasses="boostrap-table-row"
        pagination={paginationFactory({
          page,
          sizePerPage,
          totalSize,
        })}
        onTableChange={onTableChange}
      />
    </div>
  );

  function actionFormatter(cell, row) {
    return (
      <div className="action">
        <label className="labelCont">
          <input
            type="checkbox"
            name="workingMode"
            checked={selectedItems.find((x) => x === row.unitOfMeasureId)}
            onChange={(e) => handleOnSelect(row, e.target.checked)}
          />
          <span className="checkmark"></span>
        </label>
      </div>
    );
  }

  function actionHeaderFormatter(cell, rows) {
    return (
      <div className="action">
        <label className="labelCont">
          <input
            type="checkbox"
            name="workingMode"
            checked={
              unitOfMeasures &&
              unitOfMeasures.length > 0 &&
              !unitOfMeasures
                .map((p) => p.unitOfMeasureId)
                .some((p) => selectedItems.indexOf(p) < 0)
            }
            onChange={(e) => handleOnSelectAll(e.target.checked)}
          />
          <span className="checkmark"></span>
        </label>
      </div>
    );
  }

  const columns = [
    {
      dataField: "unitOfMeasureId",
      text: "",
      headerStyle: (colum, colIndex) => {
        return { width: "50px", textAlign: "center" };
      },
      headerFormatter: actionHeaderFormatter,
      formatter: actionFormatter,
      hidden: [master, admin].includes(Number(roleId)) ? false : true,
    },
    {
      dataField: "name",
      text: translator("UnitOfMeasure-TableHeader-Name"),
      sort: true,
    },
    {
      dataField: "tag",
      text: translator("UnitOfMeasure-TableHeader-Tag"),
      sort: true,
      hidden: Boolean(isMobile),
    },
  ];

  return (
    <div className="p-2">
      <div className="card container-fluid p-0">
        <div className="card-header card-wrap">
          <span>{translator("UnitOfMeasure-Title")}</span>
          {[master, admin].includes(Number(roleId)) && (
            <button
              className={
                "btn btn-primary float-right" + (isMobile ? " btn-sm" : "")
              }
              onClick={handleToAdd}
            >
              <span
                className="icon icon-cancel web-hidden"
                style={{ transform: "rotate(45deg)" }}
              ></span>{" "}
              <span className="mob-hidden">
                {translator("UnitOfMeasure-AddUnitOfMeasure")}
              </span>
            </button>
          )}
        </div>
        <div className="card-body">
          <fieldset className="scheduler-border">
            <legend className="scheduler-border">
              {translator("UnitOfMeasure-Filters")}
            </legend>
            <form
              className="row"
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <div className="form-group col-lg-3 col-md-4 col-xs-12">
                <label>{translator("UnitOfMeasure-Filters-Name")}</label>{" "}
                <Tooltip text={translator("UnitOfMeasureTooltip-Msg")} />
                <input
                  name="searchText"
                  type="text"
                  className="form-control"
                  defaultValue={filters.searchText}
                  onChange={handleInputChange}
                />
              </div>
            </form>
          </fieldset>
          {[master, admin].includes(Number(roleId)) && (
            <ListAdminButtons
              selectedItemsLength={selectedItems.length}
              handleToUpdate={handleToUpdate}
              handleDelete={() => setShowDeleteModal(true)}
            />
          )}

          {isLoading && (
            <div className="dashboard-card-spinner">
              <SpinnerLoading />
            </div>
          )}

          {!isLoading && (
            <RemoteAll
              data={unitOfMeasures}
              page={pagination.pageIndex + 1}
              sizePerPage={pagination.pageSize}
              totalSize={pagination.totalCount}
              onTableChange={handleTableChange}
            />
          )}
        </div>
      </div>

      <DeleteModal
        show={showDeleteModal}
        handleHide={handleHideDeleteModal}
        title={translator("UnitOfMeasure-DeleteModal-Title")}
        text={translator("UnitOfMeasure-DeleteModal-Text")}
        confirmDelete={confirmDelete}
      />
      <ResultModal
        show={resultModal.show}
        title={resultModal.title}
        text={resultModal.text}
        btnClass={resultModal.btnClass}
        handleClose={handleCloseResultModal}
      />
    </div>
  );
};

export default UnitOfMeasure;
